import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { StatusBadge } from "@/shared/ui/StatusBadge";
import { taskStatusLabel } from "@/shared/lib/taskStatus";
import { creationsApi, type PageResult } from "../api/creationsApi";
import {
  creationTitle,
  isTerminal,
  mergeRecent,
  nextRefetchInterval,
  waitedLabel,
  type TaskBrief,
  type TaskRow,
} from "../model/taskPolling";
import { usePersona } from "@/shared/brand/persona";

const STATUS_TYPE: Record<string, string> = {
  SUCCESS: "success",
  FAILED: "error",
  TIMEOUT: "warning",
  BLOCKED: "warning",
  RUNNING: "info",
  PENDING: "info",
};
const KIND_LABEL: Record<TaskRow["kind"], string> = { poster: "海报", video: "视频" };
const DETAIL_PATH: Record<TaskRow["kind"], string> = {
  poster: "/ai-haibao/tasks/",
  video: "/ai-video/tasks/",
};

export function RecentCreationsStrip() {
  const persona = usePersona();
  const navigate = useNavigate();
  const posterQ = useQuery<PageResult<TaskBrief>>({
    queryKey: ["advisor", "creations", "poster"],
    queryFn: () => creationsApi.listPosters(),
    refetchInterval: (q) => nextRefetchInterval(q.state.data?.records ?? []),
  });
  const videoQ = useQuery<PageResult<TaskBrief>>({
    queryKey: ["advisor", "creations", "video"],
    queryFn: () => creationsApi.listVideos(),
    refetchInterval: (q) => nextRefetchInterval(q.state.data?.records ?? []),
  });

  const rows = mergeRecent(posterQ.data?.records ?? [], videoQ.data?.records ?? [], 5);

  return (
    <div className="border-t pt-3">
      <div className="mb-2 text-xs font-medium text-muted-foreground">最近出图/出片</div>
      {rows.length === 0 ? (
        <div className="text-xs text-muted-foreground">还没有作品,问{persona.title}做一个。</div>
      ) : (
        <ul className="space-y-1">
          {rows.map((r) => (
            <li key={`${r.kind}-${r.id}`}>
              <button
                type="button"
                onClick={() => navigate(DETAIL_PATH[r.kind] + r.id)}
                className="flex w-full items-center gap-2 rounded px-1 py-1 text-left text-sm hover:bg-muted"
                title={`${KIND_LABEL[r.kind]}任务 #${r.id}`}
              >
                {/* 1004 T11:主文字是作品名(没名字才退回「海报 #id」),类型降成小标签,任务号只进 title */}
                <span className="shrink-0 rounded bg-muted px-1 text-2xs text-muted-foreground">
                  {KIND_LABEL[r.kind]}
                </span>
                <span className="min-w-0 truncate" data-testid="recent-creation-title">
                  {creationTitle(r, KIND_LABEL[r.kind])}
                </span>
                {/*
                  0830 P1-6 B 层:在途行给一个**会往上走的数**。
                  ★三位测试同学报的「卡住不动了」是个**推断** —— 屏幕上只有「处理中」三个字,
                    没有任何随时间变化的量,用户无从判断它还在不在跑。
                  ★只给在途行:终态还说「已等待」是噪声。取不到 createdAt 就整块不渲染
                    (`waitedLabel` 返 null),宁可不显示也不能出现「已等待 NaN 分钟」。
                */}
                {!isTerminal(r.status) && waitedLabel(r.createdAt, Date.now()) && (
                  <span className="text-2xs text-muted-foreground">
                    {waitedLabel(r.createdAt, Date.now())}
                  </span>
                )}
                <span className="ml-auto shrink-0">
                  <StatusBadge type={STATUS_TYPE[r.status] ?? "info"}>
                    {taskStatusLabel(r.status)}
                  </StatusBadge>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
